import React from 'react';
import { folder, useControls } from 'leva';
import { StoreState, useStore } from '@slope/store';
import { useSearchParams } from 'react-router';
import { ScoringCalcMode } from '@slope/graphql/resolvers';

function toAcronym(input: string): string {
  if (!input) return '';

  const words = input
    // normalize separators to spaces
    .replace(/[_\-\s]+/g, ' ')
    // split camelCase / PascalCase boundaries
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    // split ALLCAPS followed by Capitalized (e.g., HTTPServer)
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return words
    .map((w) => w[0])
    .join('')
    .toLowerCase();
}

const UiParamMap = {
  'liveScoring.enabled': toAcronym('liveScoring'),
  'liveScoring.calcMode': toAcronym('liveScoringCalcMode'),
  'panels.enabled': toAcronym('panels'),
  'highlights.enabled': toAcronym('highlights'),
} as const;

type UiPath = keyof typeof UiParamMap;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getPath<T extends object, K extends string>(obj: T, path: K): any {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return path.split('.').reduce<any>((acc, k) => (acc == null ? acc : acc[k]), obj);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function setPath<T extends object>(obj: T, path: string, value: any): T {
  const parts = path.split('.');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const copy: any = Array.isArray(obj) ? [...(obj as any)] : { ...(obj as any) };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let cur: any = copy;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    cur[key] = cur[key] && typeof cur[key] === 'object' ? { ...cur[key] } : {};
    cur = cur[key];
  }
  cur[parts[parts.length - 1]] = value;
  return copy;
}

function encodeUiToParams(ui: StoreState['ui'], current: URLSearchParams): URLSearchParams {
  const p = new URLSearchParams(current.toString());

  const put = (key: string, val?: string | null) => {
    if (val == null || val === '') p.delete(key);
    else p.set(key, val);
  };

  (Object.keys(UiParamMap) as UiPath[]).forEach((path) => {
    const short = UiParamMap[path];
    const val = getPath(ui, path);

    if (typeof val === 'boolean') {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      val ? put(short, '1') : p.delete(short);
      return;
    }

    put(short, val == null ? null : String(val));
  });

  return p;
}

/**
 * UserInterfaceControls component allows users to toggle UI settings such as live scoring and panels.
 * It uses Leva for controls and updates the store state and URL search parameters accordingly.
 */
export const UserInterfaceControls: React.FC = () => {
  const storeSet = useStore((state) => state.set);

  const [params, setSearchParams] = useSearchParams();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setUiAtPath = (path: UiPath, value: any) => {
    storeSet((state) => {
      const nextUi = setPath(state.ui, path, value);
      const nextParams = encodeUiToParams(nextUi, params);
      setSearchParams(nextParams);
      return { ...state, ui: nextUi };
    });
  };

  const liveScoring = useStore((state) => state.ui.liveScoring);
  const panels = useStore((state) => state.ui.panels);
  const highlights = useStore((state) => state.ui.highlights);

  useControls('UI', () => {
    return {
      'Live Scoring': folder({
        calcMode: {
          value: liveScoring.calcMode,
          onChange: (v) =>
            setUiAtPath(
              'liveScoring.calcMode',
              ScoringCalcMode[v.toUpperCase() as keyof typeof ScoringCalcMode],
            ),
          options: Object.keys(ScoringCalcMode)
            .filter((key) => isNaN(Number(key)))
            .reduce(
              (acc, key) => ({
                ...acc,
                [key.toLowerCase()]: key,
              }),
              {} as Record<string, string>,
            ),
          disabled: !liveScoring,
        },
        Enabled: {
          value: liveScoring.enabled,
          onChange: (value) => setUiAtPath('liveScoring.enabled', value),
        },
      }),
      Panels: folder({
        'Enabled ': {
          value: panels.enabled,
          onChange: (value) => setUiAtPath('panels.enabled', value),
        },
      }),
      Highlights: folder({
        'Enabled  ': {
          value: highlights.enabled,
          onChange: (value) => setUiAtPath('highlights.enabled', value),
        },
      }),
    };
  });

  return null;
};
